import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useTheme } from '../../DataStore/ThemeContext';
import functions from '../../util/functions';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import printContent from '../../util/araknerdlib';
import AlertDateSelector from '../../components/Report/AlertDateSelector';
import { useProjectReport } from '../../DataStore/ProjectReportContext';
import Signatory from '../../components/Securities/Signatory';
import LetterHead from '../../components/Securities/LetterHead';

const ProjectIncomeReportPage = () => {
    const projectId = window.location.pathname.split('/').pop(); // Extract project ID from URL

    const {currentNotify} = useTheme();
    const {getIncomeStatementProjectReport, incomeStatementProjectReport, dispatch, ISDetailLevel} = useProjectReport();
    const statementRef = useRef(); // Ref to enclose statement content for printing
    const [showAlert, setShowAlert] = useState(false);

    // Fetch trial balance data whenever the level changes
    useEffect(() => {
        getIncomeStatementProjectReport(true, null, projectId);
    }, []);

    const handleReset = ()=>{
        dispatch({ type: 'ISDetailLevel', payload: 0 });
    }

    const reduceDetail = ()=>{
        dispatch({ type: 'ISDetailLevel', payload:  ISDetailLevel + 1 });
    }

    const handleConfirmDateRange = async (range) => {
        dispatch({ type: 'dateRange', payload: range });
        await getIncomeStatementProjectReport(true, range, projectId);
      };

    return (
        <>
        <PageContainer>
            <MainContent>
                {/* Page Title */}
                <PageTitle
                    title={`${incomeStatementProjectReport?.summary?.tag?.tag_name || 'Project'} Report`}
                    description="This report shows the project's income and expenditure during a specific financial period."
                    iconClass="fas fa-chart-line"
                    sticky={true}
                    PageActions={() => (
                        <>
                            <ActionDropdownItem iconClass="fa fa-calendar-alt text-success" label="Filter Date Range" onClick={()=>setShowAlert(true)}/>
                            <ActionDropdownItem iconClass="fas fa-search-plus text-danger" label="Reduce Detail Level ..." onClick={reduceDetail}/>
                            <ActionDropdownItem iconClass="fa fas fa-binoculars text-info" label="Reset Detail Level" onClick={handleReset}/>
                            <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={()=>printContent(statementRef, "Income Statement")}/>
                            <ActionDropdownItem iconClass="fa fa-sync text-success" label="Refresh" onClick={()=>getIncomeStatementProjectReport(true, null, projectId)} />
                        </>
                    )}
                />
                {currentNotify}
                
                 {/* Statement Content */}
                 <div ref={statementRef}>
                     <LetterHead />
                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 3 }}>
                            <h4>Project Income Statement</h4>
                            <p>Summary of {incomeStatementProjectReport?.summary?.tag?.tag_name} income & expenditure</p>

                            {functions.isObject(incomeStatementProjectReport) && incomeStatementProjectReport.summary && ( <p>
                                <strong>Detail Level: </strong> {incomeStatementProjectReport.summary.level} <br/>
                                <strong>Financial Period: </strong>{' '}
                                {incomeStatementProjectReport.summary.opening_date && <>{functions.formatDate(incomeStatementProjectReport.summary.opening_date)} - {functions.formatDate(incomeStatementProjectReport.summary.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(incomeStatementProjectReport) && incomeStatementProjectReport.totals && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Total Revenue</th>
                                            <td>{functions.numberFormat(incomeStatementProjectReport.totals.total_revenues)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Expenditure</th>
                                            <td>{functions.numberFormat(incomeStatementProjectReport.totals.total_expenses)}</td>
                                        </tr>
                                        <tr>
                                            <th>Net Revenue</th>
                                            <td>{functions.numberFormat(incomeStatementProjectReport.totals.net_income)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                        <div className="table-responsive">
                        {functions.isObject(incomeStatementProjectReport) &&<><table className="table table-striped table-bordered">
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                    <tr>
                                        <th>Account Name</th>
                                        <th className="text-right">UGX</th>
                                        <th className="text-right">UGX</th>
                                    </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <th colSpan="3" className="font-weight-bold bg-light">
                                        Revenue
                                    </th>
                                </tr>
                                {incomeStatementProjectReport.income_statement.Revenues.map((item) => (
                                    <tr key={item.account_id}>
                                        <td>{item.account_name}</td>
                                        <td className="text-right">
                                        {functions.numberFormat(item.net_balance)}
                                        </td>
                                        <td>&nbsp;</td>
                                    </tr>
                                ))}
                                <tr className="font-weight-bold">
                                    <td style={{textAlign:'right'}}>Total Revenue</td>
                                    <td>&nbsp;</td>
                                    <td className="text-right text-dark">
                                        {functions.numberFormat(incomeStatementProjectReport.totals.total_revenues)}
                                    </td>
                                </tr>

                                <tr>
                                    <th colSpan="3" className="font-weight-bold bg-light">
                                        Expenses
                                    </th>
                                </tr>

                                {incomeStatementProjectReport.income_statement.Expenses.map((item) => (
                                    <tr key={item.account_id}>
                                        <td>{item.account_name}</td>
                                        <td className="text-right">
                                        {functions.numberFormat(item.net_balance)}
                                        </td>
                                        <td>&nbsp;</td>
                                    </tr>
                                ))}
                                <tr className="font-weight-bold">
                                    <th style={{textAlign:'right'}}>Total Expenses</th>
                                    <td>&nbsp;</td>
                                    <th className="text-right text-dark">
                                        {functions.numberFormat(incomeStatementProjectReport.totals.total_expenses)}
                                    </th>
                                </tr>
                                   
                                </tbody>
                                <tfoot>
                                    {/* Totals Row */}
                                    <tr>
                                        <th style={{textAlign:'right'}}>Net Revenue</th>
                                        <td>&nbsp;</td>
                                        <th className="text-right text-dark">
                                            {functions.numberFormat(incomeStatementProjectReport.totals.net_income)}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                             <Signatory />
                            </>}
                            {!incomeStatementProjectReport && <ActivityLoader />}
                            {incomeStatementProjectReport === "404" && <Notification message="Income Statement could not be loaded at this time"/>}
                            </div>
                        </div>
                    </div>


                        
                       


                    </div>

            </MainContent>
        </PageContainer>
        <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />
        </>
    );
};

export default ProjectIncomeReportPage;
